/**
 * LocalLLMPage (P3-S4 Doc 45) — Ollama health + on-prem toggle + test chat.
 */
import {
  AlertTriangle,
  CheckCircle2,
  Cpu,
  Loader2,
  RefreshCw,
  Send,
  ShieldCheck,
  XCircle,
} from "lucide-react";
import * as React from "react";

import { localLlmApi, type LocalLLMStatus } from "@/api/localLlm";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { useUIStore } from "@/stores/ui";

export function LocalLLMPage(): JSX.Element {
  const pushToast = useUIStore((s) => s.pushToast);
  const [status, setStatus] = React.useState<LocalLLMStatus | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [toggling, setToggling] = React.useState(false);

  const [prompt, setPrompt] = React.useState(
    "Bạn là AI agent tiếng Việt. Hãy chào người dùng trong 1 câu ngắn.",
  );
  const [testing, setTesting] = React.useState(false);
  const [testOutput, setTestOutput] = React.useState<{
    ok: boolean;
    text?: string;
    error?: string;
    duration_ms?: number;
  } | null>(null);

  const refresh = React.useCallback(
    async (silent = false): Promise<void> => {
      if (!silent) setLoading(true);
      try {
        const r = await localLlmApi.status();
        setStatus(r);
      } catch (e) {
        pushToast({
          variant: "destructive",
          title: "Không tải được status",
          description: e instanceof Error ? e.message : String(e),
        });
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [pushToast],
  );

  React.useEffect(() => {
    void refresh();
  }, [refresh]);

  const handleToggleOnPrem = async (next: boolean): Promise<void> => {
    setToggling(true);
    try {
      const r = await localLlmApi.setOnPrem(next);
      setStatus(r);
      pushToast({
        variant: "success",
        title: next ? "Đã bật on-prem only" : "Đã tắt on-prem only",
        description: next
          ? "Mọi cuộc trò chuyện sẽ qua Ollama. Cloud LLM bị reject."
          : "Cho phép cả cloud LLM và Ollama.",
      });
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Không thay đổi được",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setToggling(false);
    }
  };

  const handleTest = async (): Promise<void> => {
    if (!prompt.trim()) return;
    setTesting(true);
    setTestOutput(null);
    try {
      const r = await localLlmApi.testChat(prompt.trim());
      setTestOutput({
        ok: r.ok,
        text: r.text,
        error: r.error ?? undefined,
        duration_ms: r.duration_ms ?? undefined,
      });
    } catch (e) {
      setTestOutput({
        ok: false,
        error: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="container max-w-5xl py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Cpu className="size-6" /> Local LLM (Ollama)
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            On-prem inference miễn phí — chạy AI hoàn toàn trên server, không
            gọi cloud. Doc 45.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => void refresh()}>
          <RefreshCw className="size-4 mr-1.5" /> Reload
        </Button>
      </div>

      {/* Status card */}
      <Card>
        <CardContent className="p-5">
          {loading ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="size-4 animate-spin" /> Đang kiểm tra Ollama…
            </div>
          ) : status === null ? (
            <p className="text-muted-foreground">Không có dữ liệu.</p>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                {status.ok ? (
                  <CheckCircle2 className="size-5 text-green-600" />
                ) : (
                  <XCircle className="size-5 text-red-600" />
                )}
                <span className="font-semibold">
                  {status.ok ? "Online" : "Offline"}
                </span>
                <span className="text-sm text-muted-foreground">
                  · {status.base_url}
                </span>
              </div>
              {!status.ok && status.error ? (
                <p className="text-sm text-red-600">{status.error}</p>
              ) : null}
              <div className="text-sm">
                <span className="text-muted-foreground">Default model: </span>
                <code className="bg-muted px-1.5 py-0.5 rounded text-xs">
                  {status.default_model}
                </code>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Models list */}
      <Card>
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold">Models đã pull</h2>
            <span className="text-xs text-muted-foreground">
              Pull thêm: <code>docker exec aiagent-ollama ollama pull qwen2.5:7b</code>
            </span>
          </div>
          {status === null || status.models.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Chưa có model nào.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-muted-foreground border-b">
                <tr>
                  <th className="py-2">Tên</th>
                  <th className="py-2">Kích thước</th>
                  <th className="py-2">Modified</th>
                </tr>
              </thead>
              <tbody>
                {status.models.map((m) => (
                  <tr key={m.name} className="border-b last:border-0">
                    <td className="py-2 font-mono text-xs">{m.name}</td>
                    <td className="py-2 text-muted-foreground">
                      {m.size_human ?? "—"}
                    </td>
                    <td className="py-2 text-muted-foreground text-xs">
                      {m.modified_at
                        ? new Date(m.modified_at).toLocaleString("vi-VN")
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* On-prem toggle */}
      <Card>
        <CardContent className="p-5 space-y-3">
          <div className="flex items-start gap-3">
            <ShieldCheck className="size-5 text-primary mt-0.5" />
            <div className="flex-1">
              <h2 className="text-lg font-semibold">Chế độ On-Prem Only</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Khi bật, mọi cuộc trò chuyện AI sẽ chạy qua Ollama local.
                Anthropic/OpenAI/Google/DeepSeek bị reject (HTTP 403). Phù hợp
                khách hàng nhạy cảm dữ liệu hoặc muốn $0 cost.
              </p>
            </div>
          </div>
          {status?.on_prem_only ? (
            <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
              <AlertTriangle className="size-4 shrink-0" />
              <span>
                ON-PREM đang BẬT — chỉ Ollama hoạt động. Tắt nếu muốn dùng cloud.
              </span>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">
              Hiện đang TẮT — cho phép cả cloud LLM và Ollama (hybrid mode).
            </p>
          )}
          <div className="flex items-center gap-3">
            <Button
              variant={status?.on_prem_only ? "outline" : "default"}
              disabled={toggling || !status?.ok}
              onClick={() => void handleToggleOnPrem(true)}
            >
              {toggling && !status?.on_prem_only ? (
                <Loader2 className="size-4 animate-spin mr-1.5" />
              ) : null}
              Bật on-prem only
            </Button>
            <Button
              variant={status?.on_prem_only ? "default" : "outline"}
              disabled={toggling}
              onClick={() => void handleToggleOnPrem(false)}
            >
              {toggling && status?.on_prem_only ? (
                <Loader2 className="size-4 animate-spin mr-1.5" />
              ) : null}
              Tắt
            </Button>
            {!status?.ok ? (
              <span className="text-xs text-muted-foreground">
                Ollama offline — không thể bật on-prem.
              </span>
            ) : null}
          </div>
        </CardContent>
      </Card>

      {/* Test chat */}
      <Card>
        <CardContent className="p-5 space-y-3">
          <h2 className="text-lg font-semibold">Test Ollama chat</h2>
          <p className="text-sm text-muted-foreground">
            Verify model trả response. Dùng <code>{status?.default_model}</code>.
          </p>
          <Textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={3}
            placeholder="Nhập prompt..."
            disabled={testing}
          />
          <div className="flex items-center gap-2">
            <Button
              onClick={() => void handleTest()}
              disabled={testing || !prompt.trim() || !status?.ok}
            >
              {testing ? (
                <Loader2 className="size-4 animate-spin mr-1.5" />
              ) : (
                <Send className="size-4 mr-1.5" />
              )}
              Gửi test
            </Button>
            {testOutput?.duration_ms !== undefined ? (
              <span className="text-xs text-muted-foreground">
                {testOutput.duration_ms}ms
              </span>
            ) : null}
          </div>
          {testOutput ? (
            <div
              className={
                "text-sm rounded p-3 border " +
                (testOutput.ok
                  ? "bg-green-50 border-green-200"
                  : "bg-red-50 border-red-200")
              }
            >
              {testOutput.ok ? (
                <pre className="whitespace-pre-wrap font-sans">
                  {testOutput.text}
                </pre>
              ) : (
                <span className="text-red-600">{testOutput.error}</span>
              )}
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
